import React, { useEffect, useState } from 'react';
import api from '../api';
import PageHeader from '../components/PageHeader';
import { Avatar, Ic, Spinner } from '../components/ui';

const STATUS_OPTIONS = [
  { value: 'pending',   label: 'Bekliyor',     cls: 'warning', btn: 'btn-ghost' },
  { value: 'reviewing', label: 'İnceleniyor',  cls: 'info',    btn: 'btn-teal' },
  { value: 'accepted',  label: 'Kabul edildi', cls: 'success', btn: 'btn-success' },
  { value: 'rejected',  label: 'Reddedildi',   cls: 'danger',  btn: 'btn-danger' },
];
const statusMap = Object.fromEntries(STATUS_OPTIONS.map(s => [s.value, s]));

const CSS = `
.ap-wrap { display:grid; grid-template-columns:minmax(0,1fr); gap:16px; align-items:start; }
.ap-wrap.has { grid-template-columns:minmax(0,1fr) 380px; }
.ap-side { position:sticky; top:16px; }
@media (max-width: 1100px) { .ap-wrap.has { grid-template-columns:minmax(0,1fr); } .ap-side { display:none; } }
`;

function Detail({ app, onClose, onStatus, onCV }) {
  return (
    <>
      <div className="flex-between" style={{ marginBottom: 14 }}>
        <span style={{ fontWeight: 600, color: 'var(--link)' }}>Başvuru detayı</span>
        <button className="btn btn-icon" onClick={onClose} title="Kapat"><Ic n="close" s={20} /></button>
      </div>
      <div className="flex-center gap-12" style={{ marginBottom: 16 }}>
        <Avatar id={app.id} name={app.full_name || '?'} size={56} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 700 }} className="truncate">{app.full_name || '—'}</div>
          <div className="text-muted" style={{ fontSize: 13 }}>{app.bot_name} · {new Date(app.created_at).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}</div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
        {Object.entries(app.answers || {}).map(([k, v]) => (
          <div key={k} className="kv">
            <div className="k">{k}</div>
            <div className="v" style={{ fontWeight: 500 }}>{typeof v === 'string' && v.startsWith('cv_') ? '📎 CV dosyası' : String(v)}</div>
          </div>
        ))}
        {!Object.keys(app.answers || {}).length && <div className="text-muted text-sm">Cevap yok</div>}
      </div>
      {app.cv_path && <button className="btn btn-primary w-full" style={{ marginBottom: 16 }} onClick={() => onCV(app)}><Ic n="download" s={18} />CV indir</button>}
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 8 }}>Durumu güncelle</div>
      <div className="keep-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 6 }}>
        {STATUS_OPTIONS.map(s => (
          <button key={s.value} className={'btn btn-sm ' + s.btn} style={{ outline: app.status === s.value ? '2px solid var(--link)' : 'none', outlineOffset: 1 }} onClick={() => onStatus(app.id, s.value)}>
            {app.status === s.value && <Ic n="check" s={14} sw={2.6} />}{s.label}
          </button>
        ))}
      </div>
    </>
  );
}

export default function Applications() {
  const [apps, setApps] = useState([]);
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ bot_id: '', status: '' });
  const [selected, setSelected] = useState(null);
  const [narrow, setNarrow] = useState(() => window.innerWidth <= 1100);

  useEffect(() => { const h = () => setNarrow(window.innerWidth <= 1100); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (filter.bot_id) params.bot_id = filter.bot_id;
      if (filter.status) params.status = filter.status;
      const [appsRes, botsRes] = await Promise.all([api.get('/applications', { params }), api.get('/bots')]);
      setApps(appsRes.data || []); setBots(botsRes.data || []);
    } catch (e) { /* noop */ }
    setLoading(false);
  }
  useEffect(() => { load(); }, [filter]);

  async function updateStatus(id, status) {
    try { await api.patch(`/applications/${id}/status`, { status }); load(); if (selected?.id === id) setSelected({ ...selected, status }); } catch (e) { /* noop */ }
  }
  const downloadCV = app => window.open(`/api/applications/${app.id}/cv`, '_blank');

  return (
    <div className="page-enter">
      <style>{CSS}</style>
      <PageHeader title="Başvurular" subtitle={`${apps.length} başvuru`} />

      <div className="flex-between" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <div className="seg">
          {[{ value: '', label: 'Tümü' }, ...STATUS_OPTIONS].map(s => (
            <button key={s.value} className={filter.status === s.value ? 'on' : ''} onClick={() => setFilter({ ...filter, status: s.value })}>{s.label}</button>
          ))}
        </div>
        <select className="input" style={{ width: 'auto', minWidth: 180 }} value={filter.bot_id} onChange={e => setFilter({ ...filter, bot_id: e.target.value })}>
          <option value="">Tüm botlar</option>{bots.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      <div className={'ap-wrap' + (selected && !narrow ? ' has' : '')}>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner /></div>
            : !apps.length ? <div className="empty-state"><div className="icon"><Ic n="clipboard" s={44} /></div><p>Başvuru bulunamadı</p></div>
              : (
                <div className="list">
                  {apps.map(app => {
                    const s = statusMap[app.status] || { label: app.status, cls: 'neutral' };
                    return (
                      <div key={app.id} className={'list-row click' + (selected?.id === app.id ? ' on' : '')} onClick={() => setSelected(app)}>
                        <Avatar id={app.id} name={app.full_name || '?'} size={44} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="truncate" style={{ fontWeight: 600 }}>{app.full_name || '—'}</div>
                          <div className="truncate text-muted" style={{ fontSize: 13 }}>{app.bot_name} · {new Date(app.created_at).toLocaleDateString('tr-TR')}</div>
                        </div>
                        {app.cv_path && <button className="btn btn-xs btn-teal" onClick={e => { e.stopPropagation(); downloadCV(app); }} title="CV indir"><Ic n="download" s={13} />CV</button>}
                        <span className={`badge badge-${s.cls}`}>{s.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
        </div>

        {selected && !narrow && (
          <div className="card ap-side"><Detail app={selected} onClose={() => setSelected(null)} onStatus={updateStatus} onCV={downloadCV} /></div>
        )}
      </div>

      {selected && narrow && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setSelected(null)}>
          <div className="modal" style={{ maxWidth: 520, width: '100%' }}><Detail app={selected} onClose={() => setSelected(null)} onStatus={updateStatus} onCV={downloadCV} /></div>
        </div>
      )}
    </div>
  );
}
